import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { Layout, Menu, theme, Dropdown } from 'antd';
import { Content, Header } from 'antd/es/layout/layout';
import Sider from 'antd/es/layout/Sider';
import React, { useMemo, useState } from 'react';
import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import styles from './index.module.scss';
import xiaomaLogo from 'assets/images/xiaoma-logo.png';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import { useAppDispatch } from 'store/hooks';
import { useSelector } from 'react-redux';
import {
  selectGlobalInfo,
  setLanguage,
  setCollapsed,
} from 'store/slices/globalInfoSlice';
import { Trans, useTranslation } from 'react-i18next';
import {
  LOGIN_PAGE,
  setLocalStorageFirstPage,
  setLocalStorageLanguage,
} from 'commons';
import { logoutCurrentAccount } from 'commons/cached-accounts';
import Avatar from 'react-avatar';
import { checkRouteRight, getFirstMenuPath, useMenuItems } from 'commons/menus';
import LOCALS from 'commons/locals';
import useIsMobile from 'commons/hooks/useIsMobile';
import CurrentShop from 'components/current-shop';
import CurrentTimezone from 'components/current-timezone';
import PosPrinterInfo from 'components/pos-printer-info';
import TestEnvironmentBanner from 'components/test-environment-banner';
import AccountSwitcherModal from 'components/account-switcher';
import classNames from 'classnames';
import {
  SYSTEM_LANGUAGE_OPTION_LIST,
  SystemLanguageType,
} from 'constant/language';

const XiaomaLayout = () => {
  const { i18n } = useTranslation();
  const isMobile = useIsMobile();
  const userInfo = useSelector(selectUserInfo);
  const globalInfo = useSelector(selectGlobalInfo);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const menuItems = useMenuItems();
  const { collapsed } = globalInfo;
  const [accountSwitcherOpen, setAccountSwitcherOpen] = useState(false);

  const ignoreContentStyle = useMemo(() => {
    return [
      /\/oms\/order-list$/,
      /\/oms\/order-view\/\d+$/,
      /\/ums\/member-list$/,
      /\/ums\/member-view\/\d+$/,
      /\/report\/statement$/,
      /\/pms\/product-list$/,
      /\/pms\/product-add$/,
      /\/pms\/product-view\/\d+$/,
      /\/pms\/product-edit\/\d+$/,
      /\/rrs\/recycling-consignment-order$/,
      /\/sys\/admin-list$/,
      /\/fms\/account-list$/,
      /\/rrs\/recycling-consignment-detail\/\d+$/,
      /\/fms\/profit-report$/,
      /\/pms\/product-want-request$/,
    ].find((item) => item.test(pathname));
  }, [pathname]);

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const handleCollapsedChange = (newCollapsed: boolean) => {
    dispatch(setCollapsed(newCollapsed));
  };

  useEffect(() => {
    const haveRight = checkRouteRight(
      userInfo.isLoading,
      userInfo.menus,
      pathname,
    );
    if (!haveRight) {
      const firstMenuPath = getFirstMenuPath(menuItems);
      if (!firstMenuPath) {
        window.location.pathname = LOGIN_PAGE;
      } else {
        setLocalStorageFirstPage(userInfo.username, firstMenuPath);
        window.location.pathname = firstMenuPath;
      }
    }
  }, [
    menuItems,
    pathname,
    userInfo.isLoading,
    userInfo.menus,
    userInfo.username,
  ]);

  const handleLanguageChange = (language: SystemLanguageType) => {
    i18n.changeLanguage(language);
    dispatch(setLanguage(language));
    setLocalStorageLanguage(language);
    window.location.reload();
  };

  const userMenu = (
    <Menu>
      <Menu.Item
        key="personal"
        onClick={() => {
          navigate('/user-center');
        }}
      >
        <Trans i18nKey="CXvOjSxLCk" />
      </Menu.Item>
      <Menu.Item
        key="switch-account"
        onClick={() => setAccountSwitcherOpen(true)}
      >
        {i18n.t(LOCALS.switch_account)}
      </Menu.Item>
      <Menu.Divider />
      <Menu.ItemGroup title={<Trans i18nKey={LOCALS.language} />}>
        {SYSTEM_LANGUAGE_OPTION_LIST.map(({ value, label }) => (
          <Menu.Item key={value} onClick={() => handleLanguageChange(value)}>
            {label}
            {globalInfo.language === value ? ' ✓' : ''}
          </Menu.Item>
        ))}
      </Menu.ItemGroup>
      <Menu.Divider />
      <Menu.Item
        key="logout"
        onClick={() => {
          logoutCurrentAccount();
          window.location.href = LOGIN_PAGE;
        }}
      >
        <Trans i18nKey={LOCALS.log_out} />
      </Menu.Item>
    </Menu>
  );

  return (
    <Layout>
      <AccountSwitcherModal
        open={accountSwitcherOpen}
        onClose={() => setAccountSwitcherOpen(false)}
      />
      <Sider
        className={classNames(
          collapsed ? 'xm-side xm-side-collapsed' : 'xm-side xm-side-opened',
          styles.Sider,
        )}
        trigger={null}
        collapsible
        collapsed={collapsed}
      >
        {!collapsed && (
          <div
            onClick={() => {
              handleCollapsedChange(true);
            }}
            className="xl:hidden fixed top-0 left-0 right-0 bottom-0 bg-black opacity-50"
          ></div>
        )}

        <div
          className={styles.logo}
          style={{
            background: `url(${xiaomaLogo}) no-repeat 50%`,
            backgroundSize: collapsed ? '40px 40px' : '100%',
            scale: collapsed ? '1.7' : '0.8',
          }}
        ></div>
        <hr className="h-px w-full bg-slate-600" />
        <Menu
          onClick={({ key }) => {
            if (isMobile) {
              handleCollapsedChange(true);
            }
            navigate(key);
          }}
          theme="dark"
          mode="inline"
          selectedKeys={[pathname]}
          // @ts-ignore
          items={menuItems}
        />
      </Sider>
      <Layout
        style={{
          display: 'block',
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <Header
          className={styles.header}
          style={{ background: colorBgContainer }}
        >
          {React.createElement(
            collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
            {
              className: styles.trigger,
              onClick: () => handleCollapsedChange(!collapsed),
            },
          )}
          <TestEnvironmentBanner />
          <div className={styles.headerRight}>
            <div className="text-xs mr-2 flex gap-1 items-center lg:text-sm lg:gap-2">
              <PosPrinterInfo />
              <CurrentTimezone />
              <div>{userInfo.nickName}</div>
              <div>
                <CurrentShop />
              </div>
            </div>
            <Dropdown
              overlay={userMenu}
              placement="bottomRight"
              trigger={['click']}
            >
              <Avatar
                style={{
                  cursor: 'pointer',
                }}
                name={userInfo.username}
                size="36"
                round
              />
            </Dropdown>
          </div>
        </Header>
        <Content
          className={ignoreContentStyle ? 'p-2 md:p-4' : styles.content}
          style={
            ignoreContentStyle
              ? undefined
              : {
                  background: colorBgContainer,
                }
          }
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default XiaomaLayout;
